/**
 * Tutorial do modo Treino (item 5.2): oito etapas curtas que ensinam a jogar de artilheiro, na ordem em que
 * cada coisa é usada numa partida. Cada etapa arruma o campo ao começar e passa sozinha quando o jogador faz o
 * que ela pede. O aceite é um jogador novo terminar em até 5 minutos; dá para pular uma etapa.
 */
import { BALL, NET, PITCH, hoopCenters } from "@besenliga/shared";
import {
  BUTTON,
  quantizeInput,
  type AthleteState,
  type InputCommand,
  type PlayEvent,
  type Vec3,
  type WorldState,
} from "@besenliga/sim";
import { PLAYER_ID } from "../flight/ids";
import { calmWorld, dist3, type Marker, type Scenario, type ScenarioHud, type WorldAccess } from "./scenario";

const GATE_RADIUS = 4.5;
/** Tempo segurando o turbo que a etapa pede (ticks). */
const TURBO_TICKS = 45;

interface Lesson {
  id: string;
  title: string;
  text: string;
  /** Aros para atravessar em ordem. */
  gates?: Vec3[];
  enter(w: WorldState): void;
  /** Deu certo? Recebe as jogadas do tick. */
  check(t: Tutorial, w: Readonly<WorldState>, plays: readonly PlayEvent[]): boolean;
}

const me = (w: Readonly<WorldState>): AthleteState | undefined => w.athletes.find((a) => a.id === PLAYER_ID);
const mateOf = (w: Readonly<WorldState>): AthleteState | undefined =>
  w.athletes.find((a) => a.team === "casa" && a.role === "artilheiro" && a.id !== PLAYER_ID);

function place(a: AthleteState | undefined, at: Vec3, heading = { x: 1, z: 0 }): void {
  if (!a) return;
  a.position = { ...at };
  a.velocity = { x: 0, y: 0, z: 0 };
  a.heading = heading;
  a.stunTicks = 0;
}

/** Tira os outros do caminho: todos parados longe do meio, o goleiro adversário fora dos aros. */
function clearField(w: WorldState): void {
  calmWorld(w);
  w.athletes.forEach((a, i) => {
    if (a.id === PLAYER_ID) return;
    const side = a.team === "casa" ? -1 : 1;
    place(a, { x: side * (30 + (i % 4) * 6), y: 8 + (i % 3) * 6, z: (i % 2 ? 1 : -1) * 36 });
  });
  if (w.balls) {
    w.balls.pelota.holder = null;
    w.balls.pelota.position = { x: 0, y: 3, z: 40 };
    w.balls.pelota.velocity = { x: 0, y: 0, z: 0 };
  }
}

const LESSONS: Lesson[] = [
  {
    id: "voar",
    title: "Voar",
    text: "Use W A S D para voar e passe pelos três aros dourados. A vassoura vira aos poucos: para trás, ela faz a curva.",
    gates: [
      { x: -12, y: 9, z: 0 },
      { x: 6, y: 9, z: 12 },
      { x: 22, y: 9, z: -4 },
    ],
    enter(w) {
      clearField(w);
      place(me(w), { x: -30, y: 9, z: 0 });
    },
    check: (t) => t.gatesDone(),
  },
  {
    id: "altura",
    title: "Subir e descer",
    text: "Espaço sobe e Shift desce (no gamepad, RT e LT). Passe pelo aro do alto e depois pelo de baixo.",
    gates: [
      { x: 30, y: 26, z: 8 },
      { x: 12, y: 5, z: -10 },
    ],
    enter() {},
    check: (t) => t.gatesDone(),
  },
  {
    id: "turbo",
    title: "Turbo",
    text: "Segure Q (ou LB) voando para ganhar velocidade. O turbo gasta stamina: de olho na barra.",
    enter() {},
    check: (t, w) => {
      const a = me(w);
      if (a?.turbo) t.counter += 1;
      return t.counter >= TURBO_TICKS;
    },
  },
  {
    id: "pegar",
    title: "Pegar a Pelota",
    text: "A Pelota vermelha está parada à sua frente. Voe até ela: o artilheiro pega encostando.",
    enter(w) {
      const a = me(w);
      if (!a || !w.balls) return;
      w.balls.pelota.holder = null;
      w.balls.pelota.position = {
        x: a.position.x + a.heading.x * 14,
        y: Math.max(6, a.position.y),
        z: a.position.z + a.heading.z * 14,
      };
      w.balls.pelota.velocity = { x: 0, y: 0, z: 0 };
    },
    check: (_t, _w, plays) => plays.some((p) => p.kind === "posse" && p.athlete === PLAYER_ID),
  },
  {
    id: "passe",
    title: "Passe",
    text: "Vire para o companheiro marcado e dê um clique rápido (ou A) para passar. Segurar o clique é arremesso.",
    enter(w) {
      const a = me(w);
      const m = mateOf(w);
      if (!a || !m || !w.balls) return;
      w.balls.pelota.holder = a.id;
      place(m, {
        x: a.position.x + a.heading.x * 16 - a.heading.z * 8,
        y: a.position.y,
        z: a.position.z + a.heading.z * 16 + a.heading.x * 8,
      });
    },
    check: (t, w, plays) => {
      if (plays.some((p) => p.kind === "passe" && p.athlete === PLAYER_ID)) t.flag = true;
      if (plays.some((p) => p.kind === "posse" && p.athlete === mateOf(w)?.id)) return true;
      // Passe perdido: depois de 3 s a bola volta para você.
      if (t.flag && t.sinceFlag > 3 * NET.simTickHz) t.redo = true;
      return false;
    },
  },
  {
    id: "pedir",
    title: "Pedir a bola",
    text: "Agora o companheiro está com ela. Clique com o botão direito (ou B) para pedir: ele passa para você.",
    enter(w) {
      const a = me(w);
      const m = mateOf(w);
      if (!a || !m || !w.balls) return;
      // O companheiro sai de perto (você pode ter voado até ele no passe) e fica a uns 16 m, de frente.
      place(
        m,
        { x: a.position.x + a.heading.x * 16, y: a.position.y, z: a.position.z + a.heading.z * 16 },
        {
          x: -a.heading.x,
          z: -a.heading.z,
        },
      );
      w.balls.pelota.holder = m.id;
    },
    check: (t, w, plays) => {
      if (plays.some((p) => p.kind === "posse" && p.athlete === PLAYER_ID)) return true;
      // O passe escapou: depois de 3 s a bola volta para o companheiro.
      if (!w.balls?.pelota.holder) t.sinceFlag += 1;
      if (t.sinceFlag > 3 * NET.simTickHz) t.redo = true;
      return false;
    },
  },
  {
    id: "arremesso",
    title: "Arremesso",
    text: "Leve a bola até os aros da frente, segure o clique (ou X) para carregar e solte para arremessar. Gol vale 10.",
    enter(w) {
      const a = me(w);
      if (!a || !w.balls) return;
      place(a, { x: PITCH.hoopX - 34, y: 17, z: 0 });
      w.balls.pelota.holder = a.id;
      const keeper = w.athletes.find((x) => x.team === "fora" && x.role === "goleiro");
      place(keeper, { x: 40, y: 36, z: -40 });
    },
    check: (t, w, plays) => {
      if (plays.some((p) => p.kind === "gol" && p.team === "casa")) return true;
      // Errou ou perdeu a bola: depois de um tempo, ela volta para a sua mão.
      if (w.balls?.pelota.holder !== PLAYER_ID) t.sinceFlag += 1;
      else t.sinceFlag = 0;
      if (t.sinceFlag > 3 * NET.simTickHz) t.redo = true;
      return false;
    },
  },
  {
    id: "habilidade",
    title: "Habilidade",
    text: "Cada posição tem uma habilidade (E ou Y), com recarga de 12 s. A do artilheiro é a Finta Espiral: aperte E.",
    enter() {},
    check: (_t, _w, plays) => plays.some((p) => p.kind === "habilidade" && p.athlete === PLAYER_ID),
  },
];

export const TUTORIAL_LESSONS = LESSONS.map((l) => ({ id: l.id, title: l.title }));

export class Tutorial implements Scenario {
  index = 0;
  lessonTicks = 0;
  totalTicks = 0;
  gate = 0;
  counter = 0;
  flag = false;
  sinceFlag = 0;
  redo = false;
  skipped: string[] = [];

  get lesson(): Lesson | undefined {
    return LESSONS[this.index];
  }

  get finished(): boolean {
    return this.index >= LESSONS.length;
  }

  gatesDone(): boolean {
    return this.gate >= (this.lesson?.gates?.length ?? 0);
  }

  start(world: WorldAccess): void {
    this.enter(world);
  }

  private enter(world: WorldAccess): void {
    this.lessonTicks = 0;
    this.gate = 0;
    this.counter = 0;
    this.flag = false;
    this.sinceFlag = 0;
    this.redo = false;
    const l = this.lesson;
    if (l) world.edit((w) => l.enter(w));
  }

  /** O companheiro só passa quando você pede (na etapa "Pedir a bola"). */
  inputs(w: Readonly<WorldState>): Record<string, InputCommand> {
    const out: Record<string, InputCommand> = {};
    const m = mateOf(w);
    const a = me(w);
    if (!m || !a || w.balls?.pelota.holder !== m.id || this.lesson?.id !== "pedir") return out;
    const dx = a.position.x - m.position.x;
    const dz = a.position.z - m.position.z;
    const l = Math.sqrt(dx * dx + dz * dz) || 1;
    const facing = (dx * m.heading.x + dz * m.heading.z) / l;
    const called = (a.buttons & BUTTON.call) !== 0;
    out[m.id] = quantizeInput(
      (dx / l) * 0.15,
      0,
      (dz / l) * 0.15,
      called && (facing > 0.8 || l < 4) ? BUTTON.action : 0,
    );
    return out;
  }

  afterStep(world: WorldAccess, plays: readonly PlayEvent[]): void {
    const l = this.lesson;
    if (!l) return;
    this.lessonTicks += 1;
    this.totalTicks += 1;
    const w = world.state;
    // Aros: o próximo vale quando você passa perto do centro dele.
    const a = me(w);
    const g = l.gates?.[this.gate];
    if (a && g && dist3(a.position, g) < GATE_RADIUS) this.gate += 1;
    // A bola da etapa "Pegar" fica parada no ar até você chegar.
    if (l.id === "pegar" && w.balls && !w.balls.pelota.holder)
      world.edit((x) => {
        if (x.balls) x.balls.pelota.velocity = { x: 0, y: 0, z: 0 };
      });
    if (this.flag) this.sinceFlag += 1;
    if (l.check(this, world.state, plays)) {
      this.index += 1;
      this.enter(world);
      return;
    }
    if (this.redo) this.enter(world);
  }

  skip(world: WorldAccess): void {
    const l = this.lesson;
    if (!l) return;
    this.skipped.push(l.id);
    this.index += 1;
    this.enter(world);
  }

  markers(w: Readonly<WorldState>): Marker[] {
    const l = this.lesson;
    if (!l) return [];
    const out: Marker[] = (l.gates ?? []).map((g, i) => ({
      position: g,
      radius: GATE_RADIUS,
      state: i < this.gate ? "feito" : i === this.gate ? "proximo" : "depois",
    }));
    const m = mateOf(w);
    if (m && (l.id === "passe" || l.id === "pedir"))
      out.push({
        position: { x: m.position.x, y: m.position.y + 3.5, z: m.position.z },
        radius: 1.6,
        state: "proximo",
      });
    if (l.id === "arremesso") {
      const mid = hoopCenters(1)[1];
      if (mid) out.push({ position: mid, radius: PITCH.hoopRadius + 0.6, state: "proximo" });
    }
    if (l.id === "pegar" && w.balls && !w.balls.pelota.holder)
      out.push({ position: w.balls.pelota.position, radius: BALL.pelota.radius + 1.2, state: "proximo" });
    return out;
  }

  hud(): ScenarioHud {
    const l = this.lesson;
    const seconds = this.totalTicks / NET.simTickHz;
    if (!l)
      return {
        title: "Tutorial completo!",
        text: "Você já sabe o básico. Agora experimente os desafios de cada posição ou jogue uma partida.",
        progress: `${LESSONS.length} de ${LESSONS.length}`,
        seconds,
        countdown: false,
        done: true,
        summary: `Terminou em ${Math.floor(seconds / 60)} min ${String(Math.round(seconds % 60)).padStart(2, "0")} s${
          this.skipped.length
            ? ` (pulou ${this.skipped.length} etapa${this.skipped.length > 1 ? "s" : ""})`
            : ""
        }`,
      };
    const extra =
      l.gates && l.gates.length > 0
        ? ` · aro ${Math.min(this.gate + 1, l.gates.length)} de ${l.gates.length}`
        : l.id === "turbo"
          ? ` · ${Math.min(100, Math.round((this.counter / TURBO_TICKS) * 100))}%`
          : "";
    return {
      title: l.title,
      text: l.text,
      progress: `Etapa ${this.index + 1} de ${LESSONS.length}${extra}`,
      seconds,
      countdown: false,
      done: false,
    };
  }
}
